import { useMemo, useRef, useState } from 'react';
import { Badge, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { NumberInput, SegmentedControl, Select, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert, DataTable, type DataTableColumn } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import { useApplyRateImport, useFxRates, usePreviewRateImport, usePriceLadder } from '../../../../data/useSkuRates';
import { marginText } from './RateVersionModal';
import { PlanPrices } from './PlanPrices';
import { TwoLineCell } from '../../../../components/TwoLineCell';
import { formatDate, formatMinor } from '../../../../lib/format';
import { floorMinor, ladderRefusal, planAmounts } from '../../../../lib/priceLadder';
import type { Indexed } from '../../../../lib/types';
import { useT } from '../../../../i18n/I18nProvider';

type Preview = admin.RateImportPreview;
type Line = admin.RateImportLine;
type Rule = 'keep_markup' | 'keep_charge';
type Source = 'file' | 'paste';

const MUTED = 'var(--rr-color-text-muted)';
const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm';
/** The review step carries what each plan pays beside every charge, so it is wider than the presets. The modal never outgrows the screen. */
const REVIEW_WIDTH = '1200px';
/** The notes worth saying out loud. The rest-of-world choice shows in its own field. */
const SHOWN_NOTES = ['service_from_utility', 'service_not_in_file', 'country_from_region', 'authentication_international_not_applied', 'no_rest_of_world'];

/** Today in South Africa: the day the price book keeps. */
function priceBookToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' });
}

const toMajor = (minor: number) => Math.round(minor) / 100;

function suggestedCharges(lines: Line[], rule: Rule): Record<string, number | ''> {
  return Object.fromEntries(lines.map((l) => [l.sku_code, toMajor(rule === 'keep_charge' ? l.amount_keep_charge : l.amount_keep_markup)]));
}

/** "1 USD = R18.18" from how much of the currency one rand buys. */
function exchangeText(currency: string, perZar: number): string {
  return `1 ${currency} = ${formatMinor(Math.round(100 / perZar), 'ZAR')}`;
}

/**
 * Meta's WhatsApp rate card into the price book (15 September 2026).
 *
 * Two steps in one dialog. First the card: Meta's CSV, a workbook or a pasted
 * table, the day it takes effect and the exchange rate. Then every rate it
 * would set, with Meta's figure, the platform's cost before and after, and a
 * charge the operator can take as suggested (the price ladder's, or today's
 * charge) or type. Nothing is written until the last button, and its label
 * says whether the change is today or waits for a later day.
 *
 * No charge goes under its floor, the new cost plus the free plan's markup
 * (owner's ruling, 21 September 2026). Both suggestions already clear it; a
 * typed charge that does not is an error on its line and holds Apply back.
 * Each line also shows what every plan would pay at the charge in its field.
 */
export function ImportMetaRatesModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const t = useT();
  const fx = useFxRates();
  const ladder = usePriceLadder();
  const preview = usePreviewRateImport();
  const apply = useApplyRateImport();
  const fileRef = useRef<HTMLInputElement>(null);

  const [source, setSource] = useState<Source>('file');
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(priceBookToday());
  const [currency, setCurrency] = useState('auto');
  const [randPerUnit, setRandPerUnit] = useState<number | ''>('');
  const [result, setResult] = useState<Preview | null>(null);
  const [rule, setRule] = useState<Rule>('keep_markup');
  const [charges, setCharges] = useState<Record<string, number | ''>>({});
  // Floors the service answered an Apply with, by SKU. They stand over the
  // preview's: a refusal means the free plan's markup moved after the preview.
  const [refusedFloors, setRefusedFloors] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);

  const fxRows = fx.data?.data ?? [];
  const shownCurrency = currency === 'auto' ? 'USD' : currency;
  const bookRate = fxRows.find((f) => f.currency === shownCurrency)?.per_zar;

  function close() {
    setSource('file'); setFile(null); setText(''); setEffectiveFrom(priceBookToday());
    setCurrency('auto'); setRandPerUnit(''); setResult(null); setRule('keep_markup'); setCharges({}); setRefusedFloors({}); setError(null);
    onClose();
  }

  function errorText(e: unknown): string {
    const refused = ladderRefusal(e);
    if (refused?.code === 'RATE_BELOW_FLOOR' && refused.sku_code && refused.floor_minor != null) {
      const sku = refused.sku_code;
      return t('billing.skuRates.import.refusal.belowFloor', {
        name: lines.find((l) => l.sku_code === sku)?.name ?? sku,
        floor: formatMinor(refused.floor_minor, 'ZAR'),
        pct: refused.entry_markup_percent ?? entryMarkup ?? ''
      });
    }
    const message = e instanceof Error ? e.message : t('common.unknownError');
    const code = (e as { code?: unknown } | null)?.code;
    return typeof code === 'string' && code ? t(`billing.skuRates.import.errors.${code}`, message) : message;
  }

  async function runPreview(restOfWorld?: string) {
    setError(null);
    try {
      const res = await preview.mutateAsync({
        file: source === 'file' ? file ?? undefined : undefined,
        text: source === 'paste' ? text : undefined,
        currency: currency === 'auto' ? undefined : currency,
        per_zar: typeof randPerUnit === 'number' && randPerUnit > 0 ? 1 / randPerUnit : undefined,
        rest_of_world_market: restOfWorld ?? result?.rest_of_world_market ?? undefined,
        effective_from: effectiveFrom
      });
      if (!res.data) return;
      setResult(res.data);
      setCharges(suggestedCharges(res.data.lines, rule));
      setRefusedFloors({});
    } catch (e) {
      setError(errorText(e));
    }
  }

  const chargeMinor = (sku: string): number | null => {
    const v = charges[sku];
    return typeof v === 'number' ? Math.round(v * 100) : null;
  };

  const lines = result?.lines ?? [];
  // The free plan's markup as the preview read it, or as the plans page has it.
  const entryMarkup = result?.entry_markup_percent ?? ladder.entryMarkupPercent;
  const planLabels = result?.plans ?? ladder.plans;

  /** The least a line may be charged: the service's floor for its new cost, or the same arithmetic when the preview carries none. */
  const floorOf = (l: Line): number | null => {
    const previewed = l.floor_minor ?? (entryMarkup == null ? null : floorMinor(l.cost_minor_new, entryMarkup));
    const refused = refusedFloors[l.sku_code];
    return refused == null ? previewed : Math.max(previewed ?? 0, refused);
  };
  const underFloorLine = (l: Line): boolean => {
    const charge = chargeMinor(l.sku_code);
    const floor = floorOf(l);
    return charge != null && floor != null && charge < floor;
  };

  /**
   * What every plan would pay at the charge in the line's field. The service
   * priced the ladder's own figure; any other charge is priced here by the
   * same rule. A charge under its floor has no price to show: it cannot be applied.
   */
  const planAmountsOf = (l: Line): Record<string, number> | undefined => {
    const charge = chargeMinor(l.sku_code);
    if (charge == null || underFloorLine(l)) return undefined;
    if (charge === l.amount_keep_markup && l.plan_amounts_minor) return l.plan_amounts_minor;
    return ladder.all.length > 0 ? planAmounts(ladder.all, charge, l.cost_minor_new) : undefined;
  };

  const ready = lines.length > 0 && lines.every((l) => chargeMinor(l.sku_code) != null);
  const changes = lines.filter((l) => l.cost_minor_now !== l.cost_minor_new || l.amount_minor_now !== chargeMinor(l.sku_code)).length;
  const belowCost = lines.filter((l) => (chargeMinor(l.sku_code) ?? 0) < l.cost_minor_new).length;
  const underFloor = lines.filter(underFloorLine).length;

  async function runApply() {
    if (!result || !ready || underFloor > 0) return;
    setError(null);
    try {
      const res = await apply.mutateAsync({
        effective_from: result.effective_from,
        currency: result.currency,
        per_zar: result.per_zar,
        source_name: result.source_name ?? t('billing.skuRates.import.pastedText'),
        rest_of_world_market: result.rest_of_world_market ?? null,
        lines: lines.map((l) => ({ sku_code: l.sku_code, cost_minor: l.cost_minor_new, amount_minor: chargeMinor(l.sku_code) as number }))
      });
      const done = res.data;
      notifySuccess(
        t('billing.skuRates.import.toast.applied', {
          changed: done?.changed ?? 0,
          created: done?.created ?? 0,
          unchanged: done?.unchanged ?? 0,
          date: formatDate(result.effective_from)
        }),
        t('billing.skuRates.import.toast.appliedTitle')
      );
      close();
    } catch (e) {
      const refused = ladderRefusal(e);
      const sku = refused?.code === 'RATE_BELOW_FLOOR' ? refused.sku_code : undefined;
      const floor = refused?.floor_minor;
      // The line takes the service's floor from here on, so it shows the error and Apply waits for it.
      if (sku && floor != null) setRefusedFloors((prev) => ({ ...prev, [sku]: floor }));
      setError(errorText(e));
    }
  }

  const columns: DataTableColumn<Indexed<Line>>[] = useMemo(() => [
    {
      accessor: 'name',
      title: t('billing.skuRates.import.columns.item'),
      render: (l) => (
        <TwoLineCell
          primary={l.name}
          secondary={
            <>
              <code>{l.sku_code}</code>
              {l.source === 'utility' && <> · <Badge size="xs" variant="light" color="yellow">{t('billing.skuRates.import.fromUtility')}</Badge></>}
            </>
          }
          title={l.name}
        />
      )
    },
    {
      accessor: 'market',
      title: t('billing.skuRates.import.columns.market'),
      render: (l) => <Text role="bodySmall">{l.market}</Text>
    },
    {
      accessor: 'meta_rate',
      title: t('billing.skuRates.import.columns.metaRate'),
      textAlign: 'right',
      render: (l) => <Text role="bodySmall" className="rr-billing-money">{`${result?.currency ?? ''} ${l.meta_rate.toFixed(4)}`}</Text>
    },
    {
      accessor: 'cost_minor_new',
      title: t('billing.skuRates.import.columns.cost'),
      textAlign: 'right',
      render: (l) => (
        // A caption rather than a truncating second line: the old figure is
        // the point of the column, and a narrow cell cut it to "Was".
        <Stack gap="xs" align="flex-end">
          <Text role="bodySmall" fw={600} className="rr-billing-money">{formatMinor(l.cost_minor_new, 'ZAR')}</Text>
          <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>
            {l.cost_minor_now == null
              ? t('billing.skuRates.import.newRate')
              : t('billing.skuRates.import.was', { amount: formatMinor(l.cost_minor_now, 'ZAR') })}
          </Text>
        </Stack>
      )
    },
    {
      accessor: 'amount_keep_markup',
      title: t('billing.skuRates.import.columns.charge'),
      textAlign: 'right',
      render: (l) => {
        const floor = floorOf(l);
        const under = underFloorLine(l);
        return (
          <Stack gap="xs" align="flex-end">
            <NumberInput
              aria-label={t('billing.skuRates.import.chargeFor', { sku: l.sku_code })}
              value={charges[l.sku_code] ?? ''}
              onChange={(v) => setCharges((prev) => ({ ...prev, [l.sku_code]: typeof v === 'number' ? v : '' }))}
              min={0}
              step={0.01}
              style={{ width: 110 }}
              error={under && floor != null ? t('billing.skuRates.import.belowFloor', { floor: formatMinor(floor, 'ZAR') }) : undefined}
            />
            {/* The floor is the hint under the field. Under it, the error says the same figure. */}
            {!under && floor != null && floor > 0 && (
              <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>{t('billing.skuRates.floor', { amount: formatMinor(floor, 'ZAR') })}</Text>
            )}
            <Text role="caption" c={MUTED} style={{ whiteSpace: 'nowrap' }}>
              {l.amount_minor_now == null ? t('billing.skuRates.import.newRate') : t('billing.skuRates.import.was', { amount: formatMinor(l.amount_minor_now, 'ZAR') })}
            </Text>
          </Stack>
        );
      }
    },
    {
      accessor: 'plan_amounts_minor',
      title: t('billing.skuRates.columns.byPlan'),
      render: (l) => <PlanPrices amounts={planAmountsOf(l)} plans={planLabels} currency="ZAR" />
    },
    {
      accessor: 'amount_keep_charge',
      title: t('billing.skuRates.import.columns.margin'),
      textAlign: 'right',
      render: (l) => {
        const charge = chargeMinor(l.sku_code);
        if (charge == null) return null;
        const under = charge < l.cost_minor_new;
        return (
          <TwoLineCell
            primary={<Text role="bodySmall" fw={600} className="rr-billing-money" c={under ? 'var(--rr-color-error)' : undefined}>{marginText(charge, l.cost_minor_new, 'ZAR')}</Text>}
            secondary={under ? <Badge size="xs" variant="light" color="red">{t('billing.skuRates.import.belowCost')}</Badge> : undefined}
          />
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [charges, result, t, ladder, refusedFloors]);

  const sourceStep = (
    <Stack gap="md">
      <Text role="bodySmall" c={MUTED}>{t('billing.skuRates.import.intro')}</Text>
      <SegmentedControl
        size="xs"
        value={source}
        onChange={(v) => { setSource(v as Source); setError(null); }}
        data={[
          { value: 'file', label: t('billing.skuRates.import.source.file') },
          { value: 'paste', label: t('billing.skuRates.import.source.paste') }
        ]}
      />
      {source === 'file' ? (
        <Stack gap="xs">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => { setFile(e.currentTarget.files?.[0] ?? null); setError(null); e.currentTarget.value = ''; }}
          />
          <Group gap="sm">
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              {file ? t('billing.skuRates.import.file.replace') : t('billing.skuRates.import.file.choose')}
            </Button>
            <Text role="bodySmall" fw={file ? 600 : undefined} c={file ? undefined : MUTED}>
              {file ? file.name : t('billing.skuRates.import.file.none')}
            </Text>
          </Group>
          <Text role="caption" c={MUTED}>{t('billing.skuRates.import.file.hint')}</Text>
        </Stack>
      ) : (
        <Textarea
          label={t('billing.skuRates.import.paste.label')}
          description={t('billing.skuRates.import.paste.hint')}
          value={text}
          minRows={6}
          onChange={(e) => setText(e.currentTarget.value)}
        />
      )}
      <Group grow align="flex-start" gap="sm">
        <TextInput
          type="date"
          label={t('billing.skuRates.import.effectiveFrom.label')}
          description={t('billing.skuRates.import.effectiveFrom.hint')}
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.currentTarget.value)}
        />
        <Select
          label={t('billing.skuRates.import.currency.label')}
          description={t('billing.skuRates.import.currency.hint')}
          value={currency}
          onChange={(e) => setCurrency(e.currentTarget.value || 'auto')}
          data={[
            { value: 'auto', label: t('billing.skuRates.import.currency.auto') },
            ...fxRows.filter((f) => f.currency !== 'ZAR').map((f) => ({ value: f.currency, label: f.currency }))
          ]}
        />
        <NumberInput
          label={t('billing.skuRates.import.fx.label', { currency: shownCurrency })}
          description={bookRate
            ? t('billing.skuRates.import.fx.hint', { rate: exchangeText(shownCurrency, bookRate) })
            : t('billing.skuRates.import.fx.hintUnknown')}
          value={randPerUnit}
          onChange={(v) => setRandPerUnit(typeof v === 'number' ? v : '')}
          min={0}
          step={0.01}
        />
      </Group>
      {error && <Alert color="error">{error}</Alert>}
      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" onClick={close}>{t('action.cancel')}</Button>
        <Button
          loading={preview.isPending}
          disabled={source === 'file' ? !file : !text.trim() || !effectiveFrom}
          onClick={() => void runPreview()}
        >
          {t('billing.skuRates.import.preview')}
        </Button>
      </Group>
    </Stack>
  );

  const notes = (result?.notes ?? []).filter((n) => SHOWN_NOTES.includes(n));
  const reviewStep = result && (
    <Stack gap="md">
      <Group gap="xs" wrap="wrap">
        <Badge variant="light">{t('billing.skuRates.import.summary.markets', { count: result.detected.markets })}</Badge>
        <Badge variant="light" color="gray">{exchangeText(result.currency, result.per_zar)}</Badge>
        <Badge variant="light" color={result.upcoming ? 'blue' : 'green'}>
          {result.upcoming ? t('billing.skuRates.import.summary.from', { date: formatDate(result.effective_from) }) : t('billing.skuRates.import.summary.today')}
        </Badge>
        {result.source_name && <Badge variant="outline" color="gray">{result.source_name}</Badge>}
      </Group>
      {notes.map((n) => (
        <Alert key={n} color={n === 'service_from_utility' ? 'warning' : 'info'}>
          {t(`billing.skuRates.import.notes.${n}`)}
        </Alert>
      ))}
      {result.missing_countries.length > 0 && (
        <Text role="bodySmall" c={MUTED}>
          {t('billing.skuRates.import.missing', {
            countries: result.missing_countries.map((c) => t(`billing.skuRates.import.country.${c}`, c)).join(', ')
          })}
        </Text>
      )}
      <Group grow align="flex-start" gap="sm">
        <Select
          label={t('billing.skuRates.import.rest.label')}
          description={t('billing.skuRates.import.rest.hint')}
          value={result.rest_of_world_market ?? ''}
          onChange={(e) => { const market = e.currentTarget.value; if (market) void runPreview(market); }}
          data={[
            ...(result.rest_of_world_market ? [] : [{ value: '', label: t('billing.skuRates.import.rest.none') }]),
            ...result.detected.market_names.map((m) => ({ value: m, label: m }))
          ]}
        />
        <Stack gap="xs">
          <Text role="overline" c={MUTED}>{t('billing.skuRates.import.rule.label')}</Text>
          <SegmentedControl
            size="xs"
            value={rule}
            onChange={(v) => { setRule(v as Rule); setCharges(suggestedCharges(lines, v as Rule)); }}
            data={[
              { value: 'keep_markup', label: t('billing.skuRates.import.rule.keepMarkup') },
              { value: 'keep_charge', label: t('billing.skuRates.import.rule.keepCharge') }
            ]}
          />
          <Text role="caption" c={MUTED}>
            {rule === 'keep_charge'
              ? t('billing.skuRates.import.rule.keepChargeHint')
              : entryMarkup == null
                ? t('billing.skuRates.import.rule.ladderHintNoPct')
                : t('billing.skuRates.import.rule.ladderHint', { pct: entryMarkup })}
          </Text>
        </Stack>
      </Group>
      {lines.length === 0 ? (
        <Alert color="warning">{t('billing.skuRates.import.empty')}</Alert>
      ) : (
        <DataTable<Indexed<Line>>
          records={lines as Indexed<Line>[]}
          idAccessor="sku_code"
          columns={columns}
          emptyState={t('billing.skuRates.import.empty')}
        />
      )}
      {/* A charge below cost is under its floor too, so the floor's warning covers it. The older one stands in when no floor is known. */}
      {underFloor > 0
        ? <Alert color="error">{t('billing.skuRates.import.belowFloorWarning', { count: underFloor })}</Alert>
        : belowCost > 0 && <Alert color="error">{t('billing.skuRates.import.belowCostWarning', { count: belowCost })}</Alert>}
      <Text role="bodySmall" c={MUTED}>
        {result.upcoming
          ? t('billing.skuRates.import.consequence.later', { date: formatDate(result.effective_from) })
          : t('billing.skuRates.import.consequence.today')}
      </Text>
      {error && <Alert color="error">{error}</Alert>}
      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" onClick={() => { setResult(null); setError(null); }}>{t('billing.skuRates.import.back')}</Button>
        <Button loading={apply.isPending} disabled={!ready || changes === 0 || underFloor > 0} onClick={() => void runApply()}>
          {result.upcoming
            ? t('billing.skuRates.import.apply.later', { count: changes, date: formatDate(result.effective_from) })
            : t('billing.skuRates.import.apply.today', { count: changes })}
        </Button>
      </Group>
    </Stack>
  );

  return (
    <Modal opened={opened} onClose={close} title={t('billing.skuRates.import.title')} size={result ? REVIEW_WIDTH : 'lg'}>
      {result ? reviewStep : sourceStep}
    </Modal>
  );
}
